import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, LogOut, Phone, Check, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slotLabel = (s) => (s === "matin" ? "Matin (8h — 12h)" : "Après-midi (13h — 19h)");

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [appts, setAppts] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState("");

  const load = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/appointments`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setAppts(data);
    } catch {
      localStorage.removeItem("admin_token");
      setToken("");
      setAppts(null);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
    } catch {
      setError("Mot de passe incorrect.");
    }
  };

  const confirm = async (id) => {
    setConfirming(id);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/appointments/${id}/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppts(appts.map((a) => (a.id === id ? data : a)));
    } catch {
      setError("Erreur lors de la confirmation.");
    } finally {
      setConfirming("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    setError("");
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppts(appts.filter((a) => a.id !== id));
    } catch {
      setError("Erreur lors de la suppression.");
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setAppts(null);
  };

  return (
    <div className="min-h-screen px-6 md:px-12 py-32" data-testid="admin-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-20">
          <a
            href="/"
            data-testid="admin-back-link"
            className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-ash hover:text-gold transition-colors duration-500"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </a>
          {token && (
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-ash hover:text-gold transition-colors duration-500"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          )}
        </div>

        {!token ? (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="max-w-md">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Espace pro</p>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tighter leading-none mb-16" data-testid="admin-login-title">
              mes<br />
              <span className="italic font-light text-gold">rendez-vous.</span>
            </h1>
            <form onSubmit={submit => login(submit)} data-testid="admin-login-form">
              <label htmlFor="admin-login-input" className="block text-xs uppercase tracking-[0.25em] text-ash mb-3">
                Mot de passe
              </label>
              <input
                id="admin-login-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-input"
                className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-bone font-light transition-colors duration-500 rounded-none mb-10"
              />
              {error && <p className="text-xs text-red-400 tracking-wide mb-6" data-testid="admin-login-error">{error}</p>}
              <button
                type="submit"
                data-testid="admin-login-button"
                className="group relative text-xs uppercase tracking-[0.3em] text-bone hover:text-gold transition-colors duration-500"
              >
                Se connecter
                <span className="block h-px w-full bg-white/20 mt-3 overflow-hidden">
                  <span className="block h-full w-full bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out" />
                </span>
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Espace pro</p>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tighter leading-none mb-16" data-testid="admin-list-title">
              demandes de<br />
              <span className="italic font-light text-gold">rendez-vous.</span>
            </h1>

            {error && <p className="text-xs text-red-400 tracking-wide mb-6" data-testid="admin-error">{error}</p>}

            {!appts ? (
              <p className="text-sm text-ash font-light">Chargement…</p>
            ) : appts.length === 0 ? (
              <p className="text-sm text-ash font-light" data-testid="admin-empty">Aucune demande pour le moment.</p>
            ) : (
              <div>
                {appts.map((a, i) => (
                  <motion.div
                    key={a.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: i * 0.05 }}
                    className="border-b border-white/10 first:border-t py-8 flex flex-col md:flex-row md:items-center justify-between gap-6"
                    data-testid={`appointment-item-${i + 1}`}
                  >
                    <div>
                      <div className="flex items-center gap-4 mb-2">
                        <p className="font-serif text-2xl tracking-tight">{a.service}</p>
                        {a.confirmed ? (
                          <span className="text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 px-3 py-1" data-testid={`appointment-status-${i + 1}`}>
                            Confirmé
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-[0.2em] text-ash border border-white/15 px-3 py-1" data-testid={`appointment-status-${i + 1}`}>
                            À confirmer
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-bone font-light">
                        {a.date} — {slotLabel(a.slot)}
                      </p>
                      <p className="text-sm text-ash font-light mt-2">
                        {a.name} — <a href={`tel:${a.phone}`} className="hover:text-gold transition-colors duration-300">{a.phone}</a>
                      </p>
                      <p className="text-xs text-ash font-light mt-1">
                        {a.address}, {a.city}
                      </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-4">
                      {!a.confirmed && (
                        <button
                          onClick={() => confirm(a.id)}
                          disabled={confirming === a.id}
                          data-testid={`confirm-button-${i + 1}`}
                          className="inline-flex items-center gap-3 border border-gold text-gold hover:bg-gold hover:text-obsidian px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors duration-500 disabled:opacity-50"
                        >
                          <Phone className="w-4 h-4" />
                          {confirming === a.id ? "Envoi…" : "Confirmer + SMS"}
                        </button>
                      )}
                      {a.confirmed && (
                        <span className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em]">
                          <Check className="w-4 h-4" />
                          {a.sms_sent ? "SMS envoyé" : "Confirmé"}
                        </span>
                      )}
                      <button
                        onClick={() => remove(a.id)}
                        data-testid={`delete-button-${i + 1}`}
                        aria-label="Supprimer cette demande"
                        className="inline-flex items-center gap-2 text-ash hover:text-red-400 text-xs uppercase tracking-[0.2em] transition-colors duration-500"
                      >
                        <Trash2 className="w-4 h-4" />
                        Supprimer
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
